/**
 * Regeneration types and constants for cover/book regeneration feature.
 * Used by client (preview UI), server (API endpoints), and graph (state routing).
 */
import { z } from 'zod';
import { SHOT_TYPES } from './storyOutline.js';

/**
 * Canonical regeneration scope used across API, storage backup, and graph state.
 * - 'cover': Regenerate only the cover image + PDFs (reuses existing pages)
 * - 'book': Regenerate all pages, images, cover, and PDFs from the approved plan
 * - 'page': Regenerate a single targeted page image + PDFs (reuses other pages)
 */
export const REGENERATION_SCOPES = ['cover', 'book', 'page'] as const;
export type RegenerationScope = (typeof REGENERATION_SCOPES)[number];

export const regenerationScopeSchema = z.enum(REGENERATION_SCOPES);

/**
 * Regeneration limits.
 * Initial generation counts as 1, so:
 * - Cover: 3 total = 1 initial + 2 regenerations
 * - Book: 2 total = 1 initial + 1 regeneration
 * - Page: 6 total = 1 initial + 5 regenerations (per-page counter)
 */
export const COVER_REGEN_LIMIT = 3;
export const BOOK_REGEN_LIMIT = 2;
export const PAGE_REGEN_LIMIT = 6;

/**
 * Request body for POST /api/orders/:id/regenerate-page (and the admin equivalent).
 * - `pageIndex`: zero-based index of the page to regenerate.
 * - `additionalPrompt`: optional free-form text appended to the page imagePrompt
 *   to steer the regeneration (e.g. "make the pie whole, no slice in his hand").
 * - `pageOverrides`: admin-only — direct edits to the page JSON before re-running.
 *   The user-facing route strips/rejects this field; the admin route honors it.
 *   `.strict()` is used so unknown override keys are rejected at validation time.
 * - `forceFail`: MOCK_MODE-only — when true, the mock runner deliberately fails
 *   the page regeneration so E2E tests can drive the failure-restore code path
 *   (issue #500). The server gates this strictly: outside MOCK_MODE the field
 *   is ignored.
 */
export const regeneratePageRequestSchema = z.object({
  pageIndex: z.number().int().nonnegative(),
  additionalPrompt: z.string().optional(),
  pageOverrides: z
    .object({
      imagePrompt: z.string().optional(),
      text: z.string().optional(),
      shotType: z.enum(SHOT_TYPES).optional(),
      setting: z.string().optional(),
    })
    .strict()
    .partial()
    .optional(),
  forceFail: z.boolean().optional(),
});

export type RegeneratePageRequest = z.infer<typeof regeneratePageRequestSchema>;

/**
 * Request body for POST /api/orders/:id/regenerate-cover.
 *
 * Production callers POST no body — `forceFail` is MOCK_MODE-only and the
 * server ignores it strictly outside MOCK_MODE. Used by E2E tests (issue
 * #500) to drive the failure-restore banner without touching real
 * generation models.
 */
export const regenerateCoverRequestSchema = z.object({
  forceFail: z.boolean().optional(),
});

export type RegenerateCoverRequest = z.infer<
  typeof regenerateCoverRequestSchema
>;

/**
 * Request body for POST /api/orders/:id/regenerate-book.
 *
 * Production callers POST no body — `forceFail` is MOCK_MODE-only and the
 * server ignores it strictly outside MOCK_MODE. Used by E2E tests (issue
 * #500) to drive the failure-restore banner without touching real
 * generation models.
 */
export const regenerateBookRequestSchema = z.object({
  forceFail: z.boolean().optional(),
});

export type RegenerateBookRequest = z.infer<typeof regenerateBookRequestSchema>;

/**
 * Error codes returned by regeneration endpoints.
 */
export const REGENERATION_ERROR_CODES = [
  'ALREADY_PRINTING',
  'LIMIT_REACHED',
  'CHECKPOINT_UNAVAILABLE',
] as const;

export type RegenerationErrorCode = (typeof REGENERATION_ERROR_CODES)[number];

/**
 * Response from POST /api/orders/:id/regenerate-cover
 * Response from POST /api/orders/:id/regenerate-book
 */
export const regenerateResponseSchema = z.object({
  status: z.literal('started'),
});

export type RegenerateResponse = z.infer<typeof regenerateResponseSchema>;

/**
 * Error response from regeneration endpoints.
 */
export const regenerateErrorResponseSchema = z.object({
  error: z.enum(REGENERATION_ERROR_CODES),
  message: z.string(),
});

export type RegenerateErrorResponse = z.infer<
  typeof regenerateErrorResponseSchema
>;

/**
 * Failure metadata persisted on the order row when a regeneration run fails
 * but the previously-completed book has been restored from backup. Surfaces
 * the dismissible banner on `/book/preview/<id>` (issue #500).
 *
 * `failedAt` is an ISO-8601 timestamp.
 */
export const lastRegenFailureSchema = z.object({
  scope: regenerationScopeSchema,
  failureReason: z.string(),
  failureContext: z.string(),
  failureMessage: z.string().nullable(),
  failedAt: z.string(),
});

export type LastRegenFailure = z.infer<typeof lastRegenFailureSchema>;

/**
 * Durable regeneration context persisted on the order row at regen kickoff
 * and cleared on completion (success or failure-restore). Required for
 * restart-recovery — `startupRecovery.ts` reads it to reconstruct the same
 * `regenContext` the runner callbacks would have had in the in-memory cache,
 * so a server crash mid-regen still goes through the regen-aware failure
 * path instead of flipping `status` to `GENERATION_FAILED` (Decision 17).
 *
 * `pageIndex` is 1-based to match `backupOrderArtifacts`'s key naming
 * (Decision 18). The route handlers receive 0-based indexes from the
 * request body and convert exactly once at the kickoff site.
 *
 * `additionalPrompt` and `forceFail` are the original request arguments
 * preserved so a cold-resume in mock mode can recreate the exact failure
 * trigger.
 */
export const regenInFlightSchema = z.object({
  scope: regenerationScopeSchema,
  attemptNumber: z.number().int().positive(),
  decrementCount: z.boolean(),
  pageIndex: z.number().int().positive().optional(),
  priorThreadId: z.string(),
  priorWorkflowRunId: z.string(),
  priorGeneratedPath: z.string().nullable(),
  startedAt: z.string(),
  additionalPrompt: z.string().optional(),
  forceFail: z.boolean().optional(),
});

export type RegenInFlight = z.infer<typeof regenInFlightSchema>;
